import * as React from 'react';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';
// Import props.
import {
	IconButtonProps,
	ListItemPostFixSubtitleButtonProps,
	ListItemProps,
} from '@app/props';
import {
	SvgIconProps
} from '@mui/material';

interface UseListParameters<T> {
	getListItemRecord: (id: string) => T | undefined
	generateListItemElementId: (record: T) => string;
	getHeaderTitle: (record: T, index: number) => string;
	getHeaderTitleIcon?: (record: T) => React.ComponentType<SvgIconProps>;
	getSubtitle?: (record: T) => string | undefined;
	getSubtitleIcon?: (record: T) => React.ComponentType<SvgIconProps> | undefined;
	getPostFixSubtitleButonProps?: (record: T) => ListItemPostFixSubtitleButtonProps | undefined;
	getMobileIcon?: (record: T) => React.ComponentType<SvgIconProps> | undefined;
	getIcons?: (record: T) => IconButtonProps[];
	getListItemType: (record: T) => AppThemeColor;
	getListItemTypeMobile?: (record: T) => AppThemeColor | undefined;
	useMobileCard?: boolean;
	indentWithMobileIcon?: boolean;
	isCollapsible?: boolean;
	isInitiallyCollapsed?: boolean;
}

interface UseListResults {
	focusToListItemRecord: (recordId: string) => void;
	applyListItemProps: (id: string, index: number) => ListItemProps | undefined;
}

const useList = <T>(params: UseListParameters<T>): UseListResults => {

	// #region exported methods

	/**
	 * Focuses to element. 
	 **/
	const focusToListItemRecord = (recordId: string): void => {
		setQueuedListItemRecordIdToFocus(recordId);
	};

	/**
	 * Returns props for form input. 
	 **/
	const applyListItemProps = (recordId: string, index: number): ListItemProps | undefined => {
		// Fetch the relevant record for the list.
		let record: T | undefined = params.getListItemRecord(recordId);
		// Instantiate the props as undefined.
		let props: ListItemProps | undefined = undefined;
		// If there exists a record, set the props.
		if (record !== undefined) {
			// Fetch the id for the element.
			props = {
				id: params.generateListItemElementId(record),
				headerTitle: params.getHeaderTitle(record, index),
				headerTitleIcon: params.getHeaderTitleIcon?.(record) ?? undefined,
				subtitle: params.getSubtitle?.(record) ?? undefined,
				subtitleIcon: params.getSubtitleIcon?.(record) ?? undefined,
				postFixSubtitleButtonProps: params.getPostFixSubtitleButonProps?.(record) ?? undefined,
				mobileIcon: params.getMobileIcon?.(record) ?? undefined,
				icons: params.getIcons?.(record),
				listItemType: params.getListItemType(record),
				listItemTypeMobile: params.getListItemTypeMobile?.(record),
				listItemRef: (ref: React.RefObject<HTMLDivElement>) => { registerRef(recordId, ref) },
				useMobileCard: params.useMobileCard,
				indentWithMobileIcon: params.indentWithMobileIcon,
				collapsibleProps: {
					isCollapsible: params.isCollapsible ?? false,
					isInitiallyCollapsed: params.isInitiallyCollapsed ?? false,
				},
			}
		}
		return props;
	};

	// #endregion exported methods


	// #region private methods.

	/**
	 * Registers the input ref.
	 * @param elementId
	 * @param ref
	 */
	const registerRef = (recordId: string, ref: React.RefObject<HTMLDivElement>): void => {
		let listItemRef: React.RefObject<HTMLDivElement> | undefined = listItemRefs.get(recordId);
		if (listItemRef === undefined
			|| listItemRef.current === null) {
			setListItemRefs((currentListItemRefs: Map<string, React.RefObject<HTMLDivElement>>) => {
				let copiedListItemRefs: Map<string, React.RefObject<HTMLDivElement>> = new Map<string, React.RefObject<HTMLDivElement>>(currentListItemRefs);
				copiedListItemRefs.set(recordId, ref);
				return copiedListItemRefs;
			});
		}
	}

	// #endregion private methods.


	// #region variables

	const [listItemRefs, setListItemRefs] = React.useState<Map<string, React.RefObject<HTMLDivElement>>>(new Map<string, React.RefObject<HTMLDivElement>>());
	const [queuedListItemRecordIdToFocus, setQueuedListItemRecordIdToFocus] = React.useState<string | undefined>(undefined);

	// #endregion variables


	// #region life-cycle methods.

	// Called when button ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued list item focus-to flag and the reference is ready, then proceed to focus on the list item.
		if (queuedListItemRecordIdToFocus !== undefined
			&& listItemRefs.has(queuedListItemRecordIdToFocus)) {
			// Find the matching list item ref.
			let listItemRef: React.RefObject<HTMLDivElement> | undefined = listItemRefs.get(queuedListItemRecordIdToFocus);
			if (listItemRef !== undefined
				&& listItemRef.current !== null) {
				// If list item is already focused, then do not attempt to do it again.
				if (document.activeElement !== listItemRef.current) {
					listItemRef.current.focus();
				}
				// Reset the queued input to focus.
				setQueuedListItemRecordIdToFocus(undefined);
			}
		}
	}, [queuedListItemRecordIdToFocus, listItemRefs]);

	// #endregion life-cycle methods.


	return {
		focusToListItemRecord: focusToListItemRecord,
		applyListItemProps: applyListItemProps,
	};
}

// Export parameters.
export {
	UseListParameters,
}

// Export results.
export {
	UseListResults,
}

export default useList;